import * as React from "react";
import KuikaBadge, { KuikaBadgeColor } from "../../ui-kit/kuika-badge/kuika-badge";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaCodeText from "../../ui-kit/typography/CodeText/CodeText";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import {
  IKuikaEditGridColumn,
  KuikaEditGrid,
  KuikaEditGridCell,
  KuikaEditGridDetail,
  KuikaEditGridEmpty,
  KuikaEditGridRow,
  KuikaEditGridToggle
} from "../shared/kuika-edit-grid/kuika-edit-grid";

/** One field of a structure or table parameter, as the function's signature describes it. */
export interface ISapSignatureField {
  Name: string;
  Type?: string;
  /** The ABAP type. The metadata call has spelled it three ways. */
  SapType?: string;
  sapType?: string;
  SAPTYPE?: string;
  Description?: string;
}

/** One input or output of a SAP function, as its signature describes it. */
export interface ISapSignatureParam {
  Name: string;
  Type?: string;
  Text?: string;
  Optional?: boolean;
  /** A binary output the action streams as a file, with a name template and a MIME type. */
  IsFileContent?: boolean;
  /** A structure or table: its fields. */
  Fields?: ISapSignatureField[];
}

/** What the user sets on a file output: how the file is named, and what it is. */
export interface ISapFileContentOverride {
  fileNameTemplate?: string;
  mimeType?: string;
}

export type SapFileContentField = keyof ISapFileContentOverride;

/** A table (many rows) rather than a structure (one). */
export const isSapParamList = (param: ISapSignatureParam): boolean => {
  const type: string = (param?.Type ?? "").toString().toLowerCase();
  return type === "table" || type === "list" || type === "array" || type.includes("list");
};

/** One colour per family of .NET type the signature names, so a column of types can be scanned. */
export const sapTypeBadgeColor = (typeName?: string): KuikaBadgeColor => {
  const type: string = (typeName ?? "").trim().toLowerCase();
  if (!type) return "yellow";
  if (type === "string") return "green";
  if (["int", "int32", "int16", "int64", "long", "short"].includes(type)) return "blue";
  if (["byte[]", "bytes", "binary", "byte array"].includes(type)) return "pink";
  if (["object", "struct", "record"].includes(type)) return "purple";
  return "yellow";
};

const abapTypeOf = (field: ISapSignatureField): string | undefined => field.SapType || field.sapType || field.SAPTYPE;

const COLUMNS = (testIdPrefix: string): IKuikaEditGridColumn[] => [
  { id: "name", header: "Parameter", track: "minmax(0, 1.2fr)", headerTestId: `${testIdPrefix}NameText` },
  { id: "type", header: "Type", track: "minmax(96px, 0.8fr)", headerTestId: `${testIdPrefix}TypeText` },
  { id: "description", header: "Description", track: "minmax(0, 1fr)", headerTestId: `${testIdPrefix}DescriptionText` }
];

export interface ISapSignatureGridProps {
  /** "Inputs" / "Outputs" — also the grid's accessible name. */
  label: string;
  params: ISapSignatureParam[] | undefined;
  /** Said by the grid's one row when the function has none of these. */
  emptyText: string;
  /** `sapInputs` → `sapInputsNameText`, `sapInputsIT_ITEMSRow`, … */
  testIdPrefix: string;
  fileContentOverrides?: Record<string, ISapFileContentOverride>;
  onFileContentChange: (paramName: string, field: SapFileContentField, value: string) => void;
}

/**
 * A SAP function's inputs or outputs, read-only, on the datasource pages' shared grid: the name
 * with what kind of parameter it is, its type, and SAP's description of it. A structure or table
 * opens onto its fields as nested rows; a file output carries its name template and MIME type in
 * a row of its own under it. Nothing is added here — the list is the function's signature.
 */
export const SapSignatureGrid = ({
  label,
  params,
  emptyText,
  testIdPrefix,
  fileContentOverrides,
  onFileContentChange
}: ISapSignatureGridProps): JSX.Element => {
  const [openNames, setOpenNames] = React.useState<string[]>([]);
  const list: ISapSignatureParam[] = Array.isArray(params) ? params : [];

  const toggle = (name: string): void => {
    setOpenNames((open: string[]): string[] =>
      open.includes(name) ? open.filter((candidate: string): boolean => candidate !== name) : [...open, name]
    );
  };

  const renderParam = (param: ISapSignatureParam, index: number): React.ReactNode => {
    const fields: ISapSignatureField[] = Array.isArray(param.Fields) ? param.Fields : [];
    const hasFields: boolean = fields.length > 0;
    const isOpen: boolean = hasFields && openNames.includes(param.Name);
    const override: ISapFileContentOverride = fileContentOverrides?.[param.Name] ?? {};

    return (
      <React.Fragment key={`${param.Name}-${index}`}>
        <KuikaEditGridRow
          onClick={hasFields ? (): void => toggle(param.Name) : undefined}
          testId={`${testIdPrefix}${param.Name}Row`}
        >
          <KuikaEditGridCell>
            {hasFields && (
              <KuikaEditGridToggle
                expanded={isOpen}
                label={`${isOpen ? "Hide" : "Show"} the fields of ${param.Name}`}
                onToggle={(): void => toggle(param.Name)}
              />
            )}
            <span className="kuika-sap-signature__name">{param.Name}</span>
            {param.IsFileContent && <KuikaBadge content="File" color="pink" />}
            {param.Optional && <KuikaBadge content="Optional" color="orange" />}
          </KuikaEditGridCell>
          <KuikaEditGridCell>
            {hasFields && <KuikaBadge content={isSapParamList(param) ? "List" : "Object"} color="blue" />}
            {!hasFields && param.Type && <KuikaBadge content={param.Type} color={sapTypeBadgeColor(param.Type)} />}
          </KuikaEditGridCell>
          <KuikaEditGridCell>{param.Text && <KuikaSmallText>{param.Text}</KuikaSmallText>}</KuikaEditGridCell>
        </KuikaEditGridRow>

        {isOpen &&
          fields.map((field: ISapSignatureField): JSX.Element => (
            <KuikaEditGridRow key={`${param.Name}.${field.Name}`} depth={1}>
              <KuikaEditGridCell>
                <span className="kuika-sap-signature__field">{field.Name}</span>
              </KuikaEditGridCell>
              <KuikaEditGridCell>
                {field.Type && <KuikaBadge content={field.Type} color={sapTypeBadgeColor(field.Type)} />}
                {abapTypeOf(field) && <KuikaBadge content={abapTypeOf(field)} color="purple" />}
              </KuikaEditGridCell>
              <KuikaEditGridCell>
                {field.Description && <KuikaCodeText>{field.Description}</KuikaCodeText>}
              </KuikaEditGridCell>
            </KuikaEditGridRow>
          ))}

        {param.IsFileContent && (
          <KuikaEditGridDetail testId={`${testIdPrefix}${param.Name}FileContent`}>
            <KuikaInput
              size="md"
              placeholder="File name template (e.g. {ORDER_ID}_invoice.pdf)"
              aria-label={`File name template for ${param.Name}`}
              value={override.fileNameTemplate ?? ""}
              onChange={(_event: unknown, value: string | number): void =>
                onFileContentChange(param.Name, "fileNameTemplate", String(value ?? ""))
              }
            />
            <KuikaInput
              size="md"
              placeholder="MIME type (e.g. application/pdf)"
              aria-label={`MIME type for ${param.Name}`}
              value={override.mimeType ?? ""}
              onChange={(_event: unknown, value: string | number): void =>
                onFileContentChange(param.Name, "mimeType", String(value ?? ""))
              }
            />
          </KuikaEditGridDetail>
        )}
      </React.Fragment>
    );
  };

  return (
    <KuikaEditGrid columns={COLUMNS(testIdPrefix)} label={label} testId={`${testIdPrefix}Grid`}>
      {list.length === 0 ? <KuikaEditGridEmpty>{emptyText}</KuikaEditGridEmpty> : list.map(renderParam)}
    </KuikaEditGrid>
  );
};
